import { CheckIcon, ChevronsUpDown, Tags } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { readableTextColor } from '@/lib/contrast'
import { cn } from '@/lib/utils'

export interface CategoryOption {
	id: string
	name: string
	color: string
}

interface CategoryMultiSelectProps {
	options: CategoryOption[]
	value: string[]
	onChange: (next: string[]) => void
	placeholder?: string
	disabled?: boolean
	className?: string
}

export function CategoryMultiSelect({
	options,
	value,
	onChange,
	placeholder = 'Select categories…',
	disabled,
	className,
}: CategoryMultiSelectProps) {
	const [open, setOpen] = useState(false)
	const selected = useMemo(
		() => options.filter((o) => value.includes(o.id)),
		[options, value],
	)

	function toggle(id: string) {
		onChange(
			value.includes(id) ? value.filter((v) => v !== id) : [...value, id],
		)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					disabled={disabled}
					className={cn(
						'h-auto min-h-9 w-full justify-between gap-2 px-3 text-left font-normal',
						className,
					)}
				>
					<div className="flex flex-1 flex-wrap items-center gap-1.5">
						{selected.length === 0 ? (
							<span className="flex items-center gap-2 text-muted-foreground">
								<Tags className="size-4" />
								{placeholder}
							</span>
						) : (
							selected.map((cat) => (
								<Badge
									key={cat.id}
									variant="default"
									className="border-transparent"
									style={{
										backgroundColor: cat.color,
										color: readableTextColor(cat.color),
									}}
								>
									{cat.name}
								</Badge>
							))
						)}
					</div>
					<ChevronsUpDown className="size-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[var(--radix-popover-trigger-width)] p-0"
				align="start"
			>
				<Command>
					<CommandInput placeholder="Search categories…" />
					<CommandList>
						<CommandEmpty>No categories found.</CommandEmpty>
						<CommandGroup>
							{options.map((cat) => {
								const isSelected = value.includes(cat.id)
								return (
									<CommandItem
										key={cat.id}
										value={cat.name}
										onSelect={() => toggle(cat.id)}
									>
										<span
											aria-hidden
											className="size-3 rounded-full"
											style={{ backgroundColor: cat.color }}
										/>
										<span className="flex-1">{cat.name}</span>
										<CheckIcon
											className={cn(
												'size-4',
												isSelected ? 'opacity-100' : 'opacity-0',
											)}
										/>
									</CommandItem>
								)
							})}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
